import { useLocation, useParams } from "react-router-dom";
import Breadcrumbs from "../../../components/breadcrumbs/Breadcrumbs";
import endpoints from "../../../services/endpoints";
import { TableDetailsItem } from "../../../components/tables/tableFunctions";
import { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../store";
import { setPreviousUrl, setUserUrl } from "../../../store/slices/geolocationSlice";
import { resetPhoneNumbers } from "../../../store/slices/phoneNumbersSlice";
import GridTable from "../../../components/GridTable/GridTable";
import { DynamicFilterConfig } from "../../../components/DynamicComponent/DynamicFilterPanel";

interface UserManagementProps {
  type: string;
}

export default function ListOfActiveChilds({ type }: UserManagementProps) {
  const location = useLocation();
  const currentUrl = location.pathname + location.search;
  const dispatch = useDispatch<AppDispatch>();
  const { activeChilds } = endpoints;
  const [searchPlaceholder, setSearchPlaceholder] = useState<string>('user name');
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false);
  const [fetchListTrigger] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [allUsersSelected, setAllUsersSelected] = useState<boolean>(false);
  const { id } = useParams();

  const rotatePlaceholder = useCallback(() => {
    const SEARCH_PLACEHOLDERS = [
      'user name',
      'user code'
    ] as const;
    
    setIsTransitioning(true);

    setTimeout(() => {
      setSearchPlaceholder(prev => {
        const currentIndex = SEARCH_PLACEHOLDERS.indexOf(prev as typeof SEARCH_PLACEHOLDERS[number]);
        const nextIndex = (currentIndex + 1) % SEARCH_PLACEHOLDERS.length; // Loop cyclically
        return SEARCH_PLACEHOLDERS[nextIndex];
      });
      setIsTransitioning(false);
    }, 500);
  }, []);

  useEffect(() => {
    const interval = setInterval(rotatePlaceholder, 2500);
    return () => clearInterval(interval);
  }, [rotatePlaceholder]);

  const TableDetails: Record<string, TableDetailsItem> = {
    "User Code": {
      id: "user_code",
      className: "min-w-[120px]",
    },
    "User Name": {
      id: "name",
      className: "min-w-[140px]",
    },
    "Role": {
      id: "user_role",
      className: "min-w-[480px]",
    },
  };

  useEffect(() => {
    dispatch(setUserUrl(null));
    dispatch(setPreviousUrl(currentUrl));
    dispatch(resetPhoneNumbers());
  }, [currentUrl, dispatch]);

  const handleSelectionChange = (selectedIds: string[], allSelected: boolean) => {
    setSelectedUsers(selectedIds);
    setAllUsersSelected(allSelected);
  };

  const staticText = {
    emptyScreenText: {
      heading1: `There are no childrens for this user`,
      heading2: ` `,
    },
  };

  const filterOptions = () => {
    if(type === "Master Distributors") {
      return ["Distributor", "Sub Distributor", "Agent", "Retailer", "Customer"];
    } else if(type === "Distributors") {
      return ["Sub Distributor", "Agent", "Retailer", "Customer"];
    } else if(type === "Sub Distributors") {
      return ["Agent", "Retailer", "Customer"];
    }
    return ["Retailer", "Customer"];
  }

  const dynamicFilterConfig: DynamicFilterConfig = {
    "Role": {
      label: "Role",
      type: "checkbox",
      options: filterOptions(),
      required: false,
      searchKey: "user_role",
      valueMapping: {
        "Distributor": "DST",
        "Sub Distributor": "SDST",
        "Agent": "AGT",
        "Retailer": "RET",
        "Customer": "CMR",
      },
      reverseMapping: {
        "DST": "Distributor",
        "SDST": "Sub Distributor",
        "AGT": "Agent",
        "RET": "Retailer",
        "CMR": "Customer",
      },
    },
  };

  const renderSummary = ({ entireResponse }: { entireResponse: any; loading: boolean }) => {
    const totalCount = entireResponse?.total_count || 0;
    const selectedCount = selectedUsers.length;
    const isAnySelection = allUsersSelected || selectedCount > 0;
    return (
      <div className="px-1 pt-2  text-sm text-black">
        {isAnySelection && (
          <span>
            {allUsersSelected ? totalCount : selectedCount} of {totalCount}
          </span>
        )}
      </div>
    );
  };

  return (
    <div>
      <Breadcrumbs
        navPath={[
          "User Management", 
          `${type.slice(0, -1)} Management`, 
          `List of ${type}`, 
          `View ${type.slice(0, -1)} Profile`, 
          `List of Childs Users`
        ]}
        heading={`List of Childs Users`}
        noNavigation
        links={[
          `/user-management/${type.toLowerCase().replace(/ /g, '-').slice(0, -1)}-management`, 
          `/user-management/${type.toLowerCase().replace(/ /g, '-').slice(0, -1)}-management`,
          `/user-management/${type.toLowerCase().replace(/ /g, '-').slice(0, -1)}-management`, 
          `/user-management/${type.toLowerCase().replace(/ /g, '-').slice(0, -1)}-management/view-${type.toLowerCase().replace(/ /g, '-').slice(0, -1)}/${id}`
        ]}
      />
      <GridTable
        staticText={staticText}
        mainEndPoint={`${activeChilds}/${id}`}
        extraParams={`&status=${location.pathname.endsWith('/activate-childs') ? false : true}`}
        TableDetails={TableDetails}
        params={{ 'page_number': '1' }}
        searchPlaceholder={`Search by ${searchPlaceholder}`}
        placeholderClassName={`search-placeholder ${isTransitioning ? 'transitioning' : ''}`}
        filters={true}
        serviceNow={type === 'Customers' ? false : true}
        exporting={false}
        notfoundRoute={{ state: { route: `/user-management/${type.toLowerCase().replace(/ /g, '-').slice(0, -1)}-management` } }}
        title={`${type} Management`}
        fetchListTrigger={fetchListTrigger}
        enableCheckboxes={true}
        onSelectionChange={handleSelectionChange}
        renderSummary={renderSummary}
        heightClassName={(selectedUsers.length === 0  && !allUsersSelected) ? "h-childListHeight" : (selectedUsers.length < 10 && !allUsersSelected) ? "h-transactionListHeight" : "h-commentSectioneHeight"}
        dynamicFilterConfig={dynamicFilterConfig}
      />
    </div>
  );
}